/**
 * Animated, resizable shell for the docked correspondence panel (#1069).
 *
 * Animates WIDTH rather than translateX, because the point of docking is that
 * the request content is displaced. A translate would slide the panel over the
 * content and leave the layout untouched, which is the overlay behaviour we
 * moved away from.
 *
 * Width is a layout property, so this cannot use the native driver. That is
 * acceptable for a single 200ms transition on web, which is the only platform
 * that docks.
 *
 * The panel stays mounted through the closing animation and only then stops
 * rendering, so the content does not vanish while the box is still shrinking.
 *
 * ── UI-4: the drag handle ────────────────────────────────────────────────────
 *
 * Two Animated.Values, multiplied, rather than one imperatively driven value:
 *
 *   openProgress (0..1)  ×  targetWidth (px)  =  rendered width
 *
 * `openProgress` is the only thing that ever animates; `targetWidth` is set
 * instantly. That keeps the open/close transition and the drag completely off
 * each other's toes — a single value would have the 200ms timing fighting a
 * `setValue` on every pointer move, which is exactly the "animation fighting
 * the drag" the spec rules out.
 *
 * The user's chosen width is held as a DESIRED width and clamped for display,
 * never clamped into state by a viewport change. Clamping into state would
 * ratchet: narrow the window with the panel wide, widen it again, and the panel
 * would stay small forever. Drags ARE clamped into state, so dragging past a
 * stop cannot bank overshoot the user then has to drag back through.
 *
 * Web only. `shouldDock` already keeps this component off native, and the
 * handle re-checks `Platform.OS` so it cannot leak into a native tree.
 *
 * ── The handle is VISIBLE at rest (#1069 Story 7.2, D2) ─────────────────────
 *
 * ⚠️ THIS REVERSES the 2026-09-08 spec "Dock resize handle: invisible at rest,
 * revealed on hover, drag and keyboard focus", which chose invisible-at-rest on
 * a library-convention argument (VS Code, Notion, Figma). The owner has since
 * used it and could not find the edge; contact beats convention. That spec
 * carries a SUPERSEDED banner naming this story.
 *
 * What is drawn now is `ResizeGrip` — the same 36x4 pill, on its side, in the
 * same 24px hit area, from the same file as the composer's, so the two edges
 * cannot drift apart again (deferred-work `:322-324`). The hit area went 8 → 24
 * with it, which is the SC 2.5.8 finding this codebase had already logged
 * against the 8px handle; it is still absolutely positioned, so `panelWidth`
 * still means the panel and not the panel-plus-handle.
 *
 * The hover reveal is REMOVED, not demoted to an emphasis state: one state
 * fewer. Gone with it are the rest delay, the mouse/touch release and the
 * focus-origin bookkeeping, all of which existed only to keep a hover-revealed
 * line from lighting after a pointer drag. `dragging` and `focusVisible`
 * remain, and each still drives a visible state.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Animated,
  Platform,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';

import {
  clampPanelWidth,
  DOCK_PANEL_MIN_WIDTH,
  DOCK_PANEL_WIDTH,
  DOCK_RESIZE_COARSE_MULTIPLIER,
  DOCK_RESIZE_STEP,
  maxPanelWidth,
} from './dockLayout';
import { GRIP_TARGET, ResizeGrip } from './ResizeGrip';
import { useReduceMotion } from './useReduceMotion';

const DURATION_MS = 200;
/** The parts of a PointerEvent the drag actually reads. */
export interface DockPointerEvent {
  pointerId: number;
  clientX: number;
  button: number;
  /** `'mouse' | 'pen' | 'touch'` in a browser; absent from older fakes. */
  pointerType?: string;
  preventDefault: () => void;
}

/**
 * The parts of the handle's DOM node the drag actually touches. Narrowed to an
 * interface rather than `HTMLElement` so the wiring can be driven by a fake in
 * a test: this project's client suite renders through the React Native test
 * renderer, where `handleRef.current` is not a DOM node at all.
 */
export interface DockPointerTarget {
  addEventListener: (
    type: string,
    handler: (event: DockPointerEvent) => void,
  ) => void;
  removeEventListener: (
    type: string,
    handler: (event: DockPointerEvent) => void,
  ) => void;
  setPointerCapture?: (pointerId: number) => void;
  releasePointerCapture?: (pointerId: number) => void;
  focus?: () => void;
  style?: Record<string, string>;
}

/**
 * Optional lifecycle notifications for the drag. Additive: nothing about the
 * gesture changes when they are absent.
 */
export interface DockResizeDragHooks {
  /**
   * A primary-button drag has begun; fired before the handle takes focus.
   * `pointerType` is the starting event's, so the component can treat a
   * touch (no `mouseleave` will ever follow) differently from a mouse.
   */
  onDragStart?: (pointerType?: string) => void;
  /**
   * The drag ended: pointer up or cancelled, capture lost, or torn down.
   * Carries the same `pointerType` the drag started with.
   */
  onDragEnd?: (pointerType?: string) => void;
}

/**
 * Pointer drag for the resize handle. Pointer Events are a browser primitive —
 * no new dependency — and `setPointerCapture` keeps the gesture alive once the
 * cursor leaves the 8px handle, which a plain mousemove listener would drop.
 *
 * The repo's other web drag (`useWebTableRowDrag`) uses HTML5 drag-and-drop,
 * which is the wrong primitive here: it is built for transferring an item and
 * brings a drag ghost and coarse, throttled events with it.
 *
 * `getWidth` is read at pointerdown rather than passed in, so the gesture
 * starts from whatever width is current — including one the keyboard or a
 * viewport change just produced.
 *
 * Returns its own teardown.
 */
export const attachDockResizeDrag = (
  node: DockPointerTarget,
  getWidth: () => number,
  onResize: (next: number) => void,
  hooks?: DockResizeDragHooks,
): (() => void) => {
  if (node.style) {
    node.style.cursor = 'col-resize';
    node.style.userSelect = 'none';
    // Without this, touch scrolling claims the gesture before we see a move.
    node.style.touchAction = 'none';
  }

  let activePointer: number | null = null;
  let activePointerType: string | undefined;
  let startX = 0;
  let startWidth = 0;

  const onPointerDown = (event: DockPointerEvent) => {
    // Primary button only, and one pointer at a time: a second finger must not
    // hijack a drag already in progress.
    if (event.button !== 0 || activePointer !== null) return;
    event.preventDefault();
    // BEFORE `focus()`, not after: a browser dispatches the focus event
    // synchronously inside `focus()`, and the component's `onFocus` reads the
    // focus origin this notification records to tell a drag's focus from a
    // keyboard one. Notified afterwards, every drag would read as keyboard
    // focus and leave the highlight lit once the pointer let go.
    activePointerType = event.pointerType;
    hooks?.onDragStart?.(activePointerType);
    // preventDefault suppresses the browser's own focus-on-mousedown, so focus
    // has to be taken explicitly. Without this a mouse user who drags the
    // handle cannot then fine-tune with the arrow keys, and never sees the
    // focus ring — the handle would be keyboard-reachable only by tabbing.
    node.focus?.();
    activePointer = event.pointerId;
    startX = event.clientX;
    startWidth = getWidth();
    node.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event: DockPointerEvent) => {
    if (activePointer === null || event.pointerId !== activePointer) return;
    // Leading (left) edge: dragging LEFT makes the panel wider.
    onResize(startWidth - (event.clientX - startX));
  };

  const endDrag = (event: DockPointerEvent) => {
    if (activePointer === null || event.pointerId !== activePointer) return;
    node.releasePointerCapture?.(activePointer);
    activePointer = null;
    // Once per drag: the `lostpointercapture` that follows the release above
    // finds `activePointer` already null and returns at the guard.
    hooks?.onDragEnd?.(activePointerType);
  };

  node.addEventListener('pointerdown', onPointerDown);
  node.addEventListener('pointermove', onPointerMove);
  node.addEventListener('pointerup', endDrag);
  node.addEventListener('pointercancel', endDrag);
  // Capture can also be lost implicitly — a context menu, the node being
  // removed, a browser interrupt. Without this the drag would stay "active"
  // with no pointer held, and the next stray move over the handle would resize.
  node.addEventListener('lostpointercapture', endDrag);

  return () => {
    node.removeEventListener('pointerdown', onPointerDown);
    node.removeEventListener('pointermove', onPointerMove);
    node.removeEventListener('pointerup', endDrag);
    node.removeEventListener('pointercancel', endDrag);
    node.removeEventListener('lostpointercapture', endDrag);
    // Release a capture still held at teardown — the panel can close mid-drag.
    if (activePointer !== null) {
      node.releasePointerCapture?.(activePointer);
      activePointer = null;
      hooks?.onDragEnd?.(activePointerType);
    }
    if (node.style) {
      node.style.cursor = '';
      node.style.userSelect = '';
      node.style.touchAction = '';
    }
  };
};

interface Props {
  open: boolean;
  /**
   * Height of the scroll pane the dock sits in. Web only.
   *
   * Spec "Docked discussion panel: page scrollbar at the window edge"
   * (2026-09-07; that spec's "open by default" half was reversed by Story 5.3
   * on 2026-09-08 and the panel now arrives closed — the scrollbar and sticky
   * behaviour described here are the parts that were KEPT):
   * Request Detail's scroll view wraps BOTH the
   * content column and this dock, so the dock is `position: sticky` at the
   * top of that scroll view and needs an explicit height to stop at the
   * pane's bottom instead of growing with the request. The screen seeds it
   * from the window height and then measures the scroll view's `onLayout`.
   * Zero and negative values are ignored (P2): a not-yet-laid-out pane must
   * not collapse the dock to nothing.
   */
  paneHeight?: number;
  children: (rendered: boolean) => React.ReactNode;
}

export const CorrespondenceDock: React.FC<Props> = ({
  open,
  paneHeight,
  children,
}) => {
  const isWeb = Platform.OS === 'web';
  const { width: viewportWidth } = useWindowDimensions();

  /**
   * What the user asked for, NOT what fits — clamping happens on the way out.
   * Session-scoped local state: closing and reopening the panel keeps the
   * width, a reload resets it. Persisting per user across sessions would mean
   * a user preference, which is a larger change than this spec (raised on PR).
   */
  const [desiredWidth, setDesiredWidth] = useState(DOCK_PANEL_WIDTH);
  const [rendered, setRendered] = useState(open);
  // One read, shared with the list and the row (Story 7.6, D11); the pin
  // below ("draws the same resting grip under reduced motion") guards the move.
  const reduceMotion = useReduceMotion();

  /**
   * The two things that light the grip. `hovered` was a third and is gone with
   * the reveal (D2), and so is the `focusOrigin` ref that told a drag's focus
   * apart from a keyboard's: it was load-bearing ONLY for the reveal, because
   * a hover-revealed line that stayed lit after a pointer drag read as stuck.
   * With the grip drawn at rest, focus after a drag is honest — it says the
   * arrow keys will work now, which they will.
   */
  const [dragging, setDragging] = useState(false);
  const [focusVisible, setFocusVisible] = useState(false);

  const panelWidth = clampPanelWidth(desiredWidth, viewportWidth);
  const maxWidth = maxPanelWidth(viewportWidth);

  const openProgress = useRef(new Animated.Value(open ? 1 : 0)).current;
  const targetWidth = useRef(new Animated.Value(panelWidth)).current;
  const animatedWidth = useMemo(
    () => Animated.multiply(openProgress, targetWidth),
    [openProgress, targetWidth],
  );

  // Refs so the pointer listeners never need re-subscribing mid-drag. Written
  // in an effect rather than in the render body: React 19's rule is not to
  // touch `ref.current` while rendering, and a discarded or replayed render
  // would tear these — which the keyboard handler then reads.
  const panelWidthRef = useRef(panelWidth);
  const viewportRef = useRef(viewportWidth);
  useEffect(() => {
    panelWidthRef.current = panelWidth;
    viewportRef.current = viewportWidth;
  }, [panelWidth, viewportWidth]);

  const handleRef = useRef<View | null>(null);

  useEffect(() => {
    if (open) setRendered(true);

    const duration = reduceMotion ? 0 : DURATION_MS;
    const animation = Animated.timing(openProgress, {
      toValue: open ? 1 : 0,
      duration,
      useNativeDriver: false, // width is a layout property
    });

    animation.start(({ finished }) => {
      // Keep the panel mounted until it has finished collapsing, so its
      // content does not disappear mid-animation.
      if (finished && !open) setRendered(false);
    });

    return () => animation.stop();
  }, [open, reduceMotion, openProgress]);

  // Width changes — drag, keyboard, or the viewport shrinking under a wide
  // panel — apply instantly. Never animated, so nothing lags the pointer and
  // nothing fights the open/close timing.
  useEffect(() => {
    targetWidth.setValue(panelWidth);
  }, [panelWidth, targetWidth]);

  const resizeTo = useCallback((next: number) => {
    setDesiredWidth(clampPanelWidth(next, viewportRef.current));
  }, []);

  // `open && rendered`, not `rendered` alone: `rendered` stays true for the
  // 200ms collapse, and resizing a panel that is closing changes the width it
  // will reopen at for no reason the user can see.
  const handleUsable = isWeb && open && rendered;

  // A handle that unmounts while lit never fires blur, so without this the lit
  // state would come back next time the panel opens. Keyed on `rendered` as
  // well as `handleUsable`: the handle stays mounted for the 200ms collapse
  // after `handleUsable` has already gone false, and this must run again when
  // it finally leaves.
  useEffect(() => {
    if (handleUsable) return undefined;
    setDragging(false);
    setFocusVisible(false);
    return undefined;
  }, [handleUsable, rendered]);

  useEffect(() => {
    if (!handleUsable) return undefined;
    const node = handleRef.current as unknown as DockPointerTarget | null;
    if (!node?.addEventListener) return undefined;
    // The hooks are built inside the effect, closing over refs and stable
    // setters only, so their identity can never change under a live drag. A
    // `useMemo` here would be a dependency React is allowed to discard, and a
    // re-run mid-drag detaches, releases capture and aborts the gesture.
    // ⚠️ `onDragStart`/`onDragEnd` STAY, and they still have a job: `dragging`.
    // What went with the reveal is the `pointerType` argument `onDragEnd`
    // carries — it existed ONLY for the mouse-versus-touch hover release, and a
    // touch that sends no `mouseleave` is not a problem a grip drawn at rest
    // has. The helper's signature is untouched; this caller reads one argument
    // fewer.
    return attachDockResizeDrag(node, () => panelWidthRef.current, resizeTo, {
      onDragStart: () => setDragging(true),
      onDragEnd: () => setDragging(false),
    });
  }, [handleUsable, resizeTo]);

  /**
   * Keyboard operation, as a prop rather than a DOM listener so the behaviour
   * is reachable from the component test. Arrow direction matches the drag:
   * the handle is on the leading (left) edge, so LEFT widens.
   */
  const onKeyDown = useCallback(
    (event: {
      key?: string;
      shiftKey?: boolean;
      ctrlKey?: boolean;
      altKey?: boolean;
      metaKey?: boolean;
      preventDefault?: () => void;
    }) => {
      // Cmd+Left is browser-back and Alt+Arrow is history navigation. Claiming
      // those would both resize unexpectedly and swallow the user's shortcut.
      if (event.ctrlKey || event.altKey || event.metaKey) return;

      const step = event.shiftKey
        ? DOCK_RESIZE_STEP * DOCK_RESIZE_COARSE_MULTIPLIER
        : DOCK_RESIZE_STEP;
      // Steps from the width on screen, not from the remembered desired width.
      // While the viewport has the panel squeezed, the two differ; stepping
      // from the invisible one would make the first keypress appear to do
      // nothing. The cost is that a keypress while squeezed adopts the squeezed
      // width as the new intent, which is the more predictable of the two.
      const current = panelWidthRef.current;

      let next: number | null = null;
      if (event.key === 'ArrowLeft') next = current + step;
      else if (event.key === 'ArrowRight') next = current - step;
      else if (event.key === 'Home') next = maxPanelWidth(viewportRef.current);
      else if (event.key === 'End') next = DOCK_PANEL_MIN_WIDTH;
      if (next === null) return;

      event.preventDefault?.();
      // Browsers flip focus-visible on the first keypress; so does this.
      // Without it a drag (which focuses the handle) followed by arrow keys
      // would resize with no focus indicator at all.
      setFocusVisible(true);
      resizeTo(next);
    },
    [resizeTo],
  );

  // The WAI-ARIA window-splitter contract itself lives in `ResizeGrip`, built
  // from the value range handed to it there, so the composer's edge and this
  // one cannot be given different shapes of the same thing (D3). `usable` is
  // the gate that used to wrap the whole object: during the 200ms collapse the
  // handle is still mounted, and a Tab then must not focus or resize a panel
  // that is closing.

  return (
    <Animated.View
      style={[
        styles.dock,
        // Sticky inside the outer scroll view, web only: native never docks
        // and must carry none of this.
        isWeb && styles.dockWeb,
        isWeb && paneHeight != null && paneHeight > 0 && { height: paneHeight },
        { width: animatedWidth },
      ]}
      testID="correspondence-dock"
      // Hidden from assistive tech when collapsed, so a zero-width panel is not
      // reachable by screen reader or keyboard.
      accessibilityElementsHidden={!rendered}
      importantForAccessibility={rendered ? 'auto' : 'no-hide-descendants'}
      pointerEvents={rendered ? 'auto' : 'none'}
    >
      {/*
       * Stable-width sleeve, and the thing the panel sizes itself against.
       *
       * The shell above animates 0 -> panelWidth and clips; this does NOT
       * animate, so the panel keeps its true size throughout the open
       * transition instead of reflowing a frame at a time. That is why
       * `CorrespondencePanel`'s `panelDocked` can safely be `width: '100%'`:
       * it resolves against a width that is stable during the animation, which
       * is the exact property the old fixed 400px was protecting.
       *
       * The panel MUST fill this rather than carry its own fixed width — a
       * child with an explicit width does not stretch to a wider parent in
       * react-native-web, which showed up as a widened panel with its content
       * stuck at 400px and dead space beside it.
       */}
      <View style={styles.clip} testID="correspondence-dock-clip">
        <View
          style={[styles.sleeve, { width: panelWidth }]}
          testID="correspondence-dock-sleeve"
        >
          {children(rendered)}
        </View>
      </View>
      {isWeb && rendered ? (
        <ResizeGrip
          direction="vertical"
          testIDPrefix="correspondence-dock-resize"
          name="Resize discussion panel"
          value={panelWidth}
          min={DOCK_PANEL_MIN_WIDTH}
          max={maxWidth}
          valueText={`${panelWidth} pixels wide`}
          dragging={dragging}
          focusVisible={focusVisible}
          usable={handleUsable}
          style={styles.handle}
          handleRef={handleRef}
          onKeyDown={onKeyDown}
          onFocus={() => setFocusVisible(true)}
          onBlur={() => setFocusVisible(false)}
        />
      ) : null}
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  /**
   * ⚠️ THE SHELL NO LONGER CLIPS. `overflow: 'hidden'` moved to `clip` below,
   * an inner layer of the same size, so that the resize grip — a SIBLING of
   * that layer — can hang outside the dock's leading edge without being cut in
   * half. See `handle`.
   *
   * Clipping behaviour is unchanged: `clip` is `width: '100%'` of the same
   * animating box, so it narrows with it exactly as the shell used to.
   */
  dock: {
    flexGrow: 0,
    flexShrink: 0,
  },
  /**
   * Clips the panel while it is narrower than its content, so text does not
   * spill across the request during the transition. This is the job the shell
   * used to do itself.
   */
  clip: {
    overflow: 'hidden',
    flex: 1,
    width: '100%',
  },
  /**
   * Pins the dock to the top of Request Detail's outer scroll view while the
   * content column scrolls past it (spec 2026-09-07). `alignSelf: 'flex-start'`
   * keeps a `stretch` row from making the dock as tall as the content, which
   * would leave sticky nothing to do. Cast like the existing web-only
   * `overflow: 'hidden' as any` in `RequestDetail.tsx`: react-native-web 0.21
   * renders `sticky`, React Native's style types do not name it.
   */
  dockWeb: {
    position: 'sticky' as any,
    top: 0,
    alignSelf: 'flex-start',
  },
  /**
   * `flex: 1` is safe here where it would not be on the panel: the dock is a
   * COLUMN container, so it sets the sleeve's height, and width stays free for
   * the explicit `panelWidth` above.
   */
  sleeve: {
    flex: 1,
  },
  /**
   * WHERE THE HANDLE SITS. Its WIDTH and its pill come from `ResizeGrip` (D3,
   * D5): 24 on the short axis, the SC 2.5.8 target the composer's grip was
   * built to and the 8px handle was logged for missing.
   *
   * ⚠️ STILL ABSOLUTELY POSITIONED, and that is the part the widening must not
   * disturb. A flex sibling would make `panelWidth` mean "panel plus handle",
   * which is not the quantity `dockLayout.ts`'s content-floor arithmetic is
   * written against; 24 absolute pixels cost the panel exactly as much layout
   * width as 8 absolute pixels did, which is none.
   *
   * The pill is CENTRED in the target rather than pinned to the leading edge —
   * the old 2px line sat on the panel's 1px border because it WAS the divider's
   * emphasis. A grabber is not a divider, and the composer's sits centred.
   */
  handle: {
    position: 'absolute',
    // CENTRED ON THE DOCK'S LEADING EDGE, not tucked inside it (owner,
    // 2026-09-18: the pill "covers too much of the conversation row"). Half the
    // target hangs over the request, half over the panel, so the pill's centre
    // line IS the seam between them — which is the thing being dragged, and the
    // reading the composer's grip already has at the card's top edge.
    //
    // ⚠️ This is why `dock` no longer carries `overflow: 'hidden'`. With the
    // shell clipping, the outer half of both target and pill was cut away.
    left: -(GRIP_TARGET / 2),
    top: 0,
    bottom: 0,
    zIndex: 2,
  },
});
